import React, { useCallback, useEffect, useRef, useState } from 'react';

import { isUsedOnMobile, joinClassNames } from '../../util/util';
import { useConfiguration } from '../../contexts/ConfigurationContext';

interface ControlButtonProps {
	className?: string;
	tabIndex?: number;
	role?: string;
	ariaLabel?: string;
	style?: any;
	hoverStyle?: any;
	icon?: React.ReactNode;
	children?: React.ReactNode;
	tooltip?: string;
	vertical?: boolean;
	topControl?: boolean;
	lightTooltip?: boolean;
	tooltipStyle?: any;
	autoFocus?: boolean;
	disabled?: boolean;
	onClick?: (_e: React.MouseEvent<HTMLElement>, _control?: Function) => void;
	onMouseDown?: (_e: React.MouseEvent<HTMLElement>) => void;
	onMouseEnter?: (_e: React.MouseEvent<HTMLElement>) => void;
	onMouseLeave?: (_e: React.MouseEvent<HTMLElement>) => void;
	onKeyDown?: (_e: React.KeyboardEvent<HTMLElement>) => void;
	onBlur?: (_e: React.FocusEvent<HTMLElement>) => void;
	onFocus?: (_e: React.FocusEvent<HTMLElement>) => void;
}

const ControlButton: React.FC<ControlButtonProps> = ({
	className,
	tabIndex,
	role,
	ariaLabel,
	style,
	hoverStyle,
	onClick,
	icon,
	children,
	tooltip,
	onMouseDown,
	onMouseEnter,
	onMouseLeave,
	onKeyDown,
	onFocus,
	onBlur,
	vertical,
	topControl,
	lightTooltip = true,
	tooltipStyle,
	autoFocus = false,
	...rest
}) => {
	const [isHovered, setIsHovered] = useState(false);
	const fadeoutTimeOut = useRef<any>();
	const btnRef = useRef<HTMLButtonElement>(null);
	const { configParams } = useConfiguration();

	const setHoverToTrue = useCallback(() => {
		if (isUsedOnMobile(configParams)) {
			return;
		}

		setIsHovered(true);
	}, [configParams]);

	const setHoverToFalse = useCallback(() => {
		if (isUsedOnMobile(configParams)) {
			return;
		}

		setIsHovered(false);
	}, [configParams]);

	const handleOnClick = useCallback(
		(e) => {
			e.stopPropagation();
			e.preventDefault();
			onClick?.(e, setHoverToFalse);
		},
		[onClick, setHoverToFalse]
	);

	const handleOnKeyDown = useCallback(
		(e) => {
			if (isUsedOnMobile(configParams)) {
				return;
			}

			if (e.key === 'Escape') {
				setHoverToFalse();
				return;
			}

			if (onClick && (e.key === 'Enter' || e.key === 'Space' || e.keyCode === 32 || e.keyCode === 13)) {
				handleOnClick(e);
			}
			onKeyDown?.(e);
		},
		[handleOnClick, onClick, onKeyDown, configParams, setHoverToFalse]
	);

	const handleOnMouseDown = useCallback(
		(e) => {
			onMouseDown?.(e);
		},
		[onMouseDown]
	);

	const handleOnMouseEnter = useCallback(
		(e) => {
			clearTimeout(fadeoutTimeOut.current);
			setHoverToTrue();
			onMouseEnter?.(e);
		},
		[onMouseEnter, setHoverToTrue]
	);

	const handleOnMouseLeave = useCallback(
		(e) => {
			fadeoutTimeOut.current = setTimeout(() => {
				setHoverToFalse();
				onMouseLeave?.(e);
			}, 50);
		},
		[onMouseLeave, setHoverToFalse]
	);

	const handleOnFocus = useCallback(
		(e) => {
			setHoverToTrue();
			onFocus?.(e);
		},
		[onFocus, setHoverToTrue]
	);

	const handleOnBlur = useCallback(
		(e) => {
			setHoverToFalse();
			onBlur?.(e);
		},
		[onBlur, setHoverToFalse]
	);

	useEffect(() => {
		if (autoFocus) {
			btnRef.current?.focus();
		}
	}, [autoFocus]);

	return (
		<button
			onDoubleClick={(e) => {
				e.stopPropagation();
				e.preventDefault();
			}}
			className={joinClassNames(
				['controlbar__control', vertical ? 'controlbar__control--vertical' : '', className],
				topControl ? 'controlbar__control--top' : ''
			)}
			tabIndex={tabIndex}
			role={role}
			aria-label={ariaLabel ?? tooltip}
			style={{ ...(style ?? {}), ...(isHovered ? hoverStyle ?? {} : {}) }}
			onClick={handleOnClick}
			onKeyDown={handleOnKeyDown}
			onMouseDown={handleOnMouseDown}
			onMouseEnter={handleOnMouseEnter}
			onMouseLeave={handleOnMouseLeave}
			onFocus={handleOnFocus}
			onBlur={handleOnBlur}
			ref={btnRef}
			disabled={rest.disabled}
			{...rest}
		>
			{icon}
			{children}
			{tooltip && (
				<div
					aria-hidden="true"
					style={tooltipStyle}
					className={joinClassNames([
						'controlbar__control-tooltip',
						lightTooltip ? 'controlbar__control-tooltip--light' : '',
						isHovered ? 'controlbar__control-tooltip--visible' : '',
					])}
					onMouseEnter={handleOnMouseEnter}
					onMouseLeave={handleOnMouseLeave}
				>
					<div className="controlbar__control-tooltip-text">{tooltip}</div>
				</div>
			)}
		</button>
	);
};

export default ControlButton;
